import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, PlayCircle, CursorClick, ArrowSquareOut, VideoCamera } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VideoPageCard } from "./VideoPageCard";

const HOW = [
  { icon: PlayCircle, t: "Vídeo em destaque" },
  { icon: CursorClick, t: "Botão de compra · Pix ou Cartão" },
  { icon: ArrowSquareOut, t: "Pagou → vai para o seu link" },
];

export const VideoPagesSection = () => {
  const [pages, setPages] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/video-pages").then(({ data }) => setPages(data.pages || [])).catch(() => setPages([]));
  useEffect(() => { load(); }, []);

  const toggle = async (p, active) => {
    setPages((ps) => ps.map((x) => (x.id === p.id ? { ...x, active } : x)));
    try {
      await api.patch(`/video-pages/${p.id}/active`, { active });
      toast.success(active ? "Página ativada" : "Página pausada");
    } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/video-pages/${toDelete.id}`);
      toast.success("Página excluída");
      setPages((ps) => ps.filter((x) => x.id !== toDelete.id));
    } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <section className="mt-16" data-testid="video-pages-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-caps mb-2 flex items-center gap-2">Venda direta <span className="rounded-full bg-[var(--accent)] text-black text-[9px] font-black px-2 py-0.5">NOVO</span></div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter">Páginas de Vídeo + Compra</h2>
          <p className="text-white/60 mt-2 text-sm sm:text-base max-w-xl">Um vídeo, um botão de compra logo abaixo e, após o pagamento, o cliente é levado direto para o link que você escolher.</p>
        </div>
        <Link to="/app/video-pages/new" className="btn-primary justify-center shrink-0" data-testid="create-video-page-btn">
          <Plus size={18} weight="bold" /> Nova página
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {HOW.map(({ icon: Icon, t }, i) => (
          <span key={t} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70">
            <span className="w-5 h-5 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] text-[10px] font-black flex items-center justify-center">{i + 1}</span>
            <Icon size={14} /> {t}
          </span>
        ))}
      </div>

      {pages === null ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">{[0, 1].map((k) => <div key={k} className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" />)}</div>
      ) : pages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-10 sm:p-14 text-center" data-testid="video-pages-empty">
          <VideoCamera size={44} color="var(--accent)" weight="duotone" className="mx-auto" />
          <div className="font-display font-bold text-xl sm:text-2xl mt-4">Crie sua primeira página de vídeo</div>
          <div className="text-white/60 mt-2 text-sm">Pronta em 1 minuto. Funciona perfeitamente no celular, tablet e computador.</div>
          <Link to="/app/video-pages/new" className="btn-primary inline-flex mt-6" data-testid="video-pages-empty-create-btn"><Plus size={18} weight="bold" /> Criar página</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {pages.map((p) => <VideoPageCard key={p.id} p={p} onToggle={toggle} onDelete={setToDelete} />)}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#121824] border-white/10 text-[#F8FAFC] rounded-2xl" data-testid="video-page-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta página?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.title}” sairá do ar imediatamente. O histórico de vendas continua no seu extrato.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="video-page-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="video-page-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
